import type { CheatType } from '../trainers';

// Not 100% inclusive as they are mod-driven and there is not a centralized type / source for these yet.
export type SessionEventCategory =
    | 'deaths'
    | 'general'
    | 'kills'
    | 'objective_success'
    | 'objective_fail'
    | 'achievements'
    | 'mod';

/**
 * @example { value: 1, cheat_type: "toggle" }
 * @example { value: 4, oldValue: 3, cheat_type: "number" }
 */
export interface CheatSessionEventPayload {
    value: number;
    oldValue?: number;
    cheat_type: CheatType;
}

export interface GameSessionEventPayload {
    value: number;
}

export type SessionEventPayload = CheatSessionEventPayload | GameSessionEventPayload;

/**
 * Raw session event from the analytics API (BigQuery).
 * All values are strings since they come directly from the query response.
 */
export interface RawSessionEvent {
    ts: string | null;
    x: string | null;
    y: string | null;
    z: string | null;
    event_type: string | null;
    event_key: string | null;
    event_category: string | null;
    event_name: string | null;
    category_name: string | null;
    event_payload: Record<string, unknown> | null;
}

/** Normalized session event with parsed numeric types. */
export interface SessionEventInternal {
    timestamp: number;
    x: number;
    y: number;
    z: number;
    eventType: string;
    eventKey: string;
    eventCategory: SessionEventCategory;
    eventName: string;
    categoryName: string;
    payload: SessionEventPayload;
}

export interface SessionResponseSectionMetadata {
    totalRows: string;
}

export interface GetSessionResponse {
    data: {
        positions: Array<{
            ts: string;
            x: string;
            y: string;
            z: string;
        }>;
        events?: RawSessionEvent[];
    };
    metadata: {
        positions: SessionResponseSectionMetadata;
        events?: SessionResponseSectionMetadata;
        isShared: boolean;
    };
}

export type GameSession = GetSessionResponse['data'] & {
    uuid: string;
    isShared: boolean;
};

export interface PublicSessionResponseSessionMetadata {
    sessionId: string;
    titleId: number;
    startTime: string;
    endTime: string;
}

export interface PublicSessionResponse {
    data: {
        positions: Array<{
            ts: string;
            x: string;
            y: string;
            z: string;
        }>;
        events: RawSessionEvent[] | null;
    };
    metadata: {
        session: PublicSessionResponseSessionMetadata;
        positions: SessionResponseSectionMetadata;
        events: SessionResponseSectionMetadata;
    };
}

export interface ApiSession {
    uuid: string;
    gameId: number;
    titleId: number;
    titleSlug: string | null;
    titleName: string | null;
    name: string | null;
    status: string;
    startedAt: string;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface PaginationLinks {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
}

export interface ListSessionsResponse {
    data: ApiSession[];
    links: PaginationLinks;
    meta: PaginationInfo;
}

export interface PaginationInfo {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    from: number | null;
    to: number | null;
}

export interface SessionSharingResponse {
    is_shareable: boolean;
    share_url?: string; // Do not use this as the user-presented share URL, it is the API URL to fetch public data.
}

export interface SessionOwnerResponse {
    username: string;
    profileImageUrl: string | null;
}

export interface SessionVideo {
    externalId: string;
    title: string;
    privacy: string;
    recordedAt: string;
    thumbnailUrl: string;
    playbackUrl: string;
    downloadUrl: string | null;
}

export interface SessionVideosResponse {
    videos: SessionVideo[];
}

export interface SessionTrailPoint {
    x: number;
    y: number;
    z: number;
    timestamp: number;
}

export interface GameSessionInternal {
    uuid: string;
    name: string | null;
    titleId: number;
    titleName: string | null;
    startedAt: string;
    completedAt: string | null;
    status: string;
    trail: SessionTrailPoint[];
    events: SessionEventInternal[];
    /** Duration in milliseconds */
    duration: number;
    isShared: boolean;
    hasPositions?: boolean;
    /** Whether this session exists in Firestore (present in the metadata /list response). */
    hasFirestoreData?: boolean;
}

function parseEventPayload(raw: Record<string, unknown> | null): SessionEventPayload {
    if (!raw) {
        return { value: 0 };
    }

    if ('cheat_type' in raw && typeof raw.cheat_type === 'string') {
        return {
            value: typeof raw.value === 'number' ? raw.value : 0,
            oldValue: typeof raw.oldValue === 'number' ? raw.oldValue : undefined,
            cheat_type: raw.cheat_type as CheatType,
        };
    }

    return {
        value: typeof raw.value === 'number' ? raw.value : 0,
    };
}

function transformSessionEvents(rawEvents?: RawSessionEvent[]): SessionEventInternal[] {
    if (!rawEvents) {
        return [];
    }

    return rawEvents
        .filter(
            (event): event is RawSessionEvent & { event_type: string; event_key: string } =>
                event.event_type != null && event.event_key != null,
        )
        .map(event => ({
            timestamp: event.ts ? parseFloat(event.ts) * 1000 : 0,
            x: event.x ? parseFloat(event.x) : 0,
            y: event.y ? parseFloat(event.y) : 0,
            z: event.z ? parseFloat(event.z) : 0,
            eventType: event.event_type,
            eventKey: event.event_key,
            eventCategory: (event.event_category ?? 'general') as SessionEventCategory,
            eventName: event.event_name ?? event.event_key,
            categoryName: event.category_name ?? event.event_category ?? 'General',
            payload: parseEventPayload(event.event_payload),
        }));
}

export interface UserSessionListItem {
    sessionId: string;
    titleId: number;
    startTime: string;
    endTime: string;
    isShared: boolean;
    positionCount: number;
    eventCount: number;
}

export interface UserSessionListResponse {
    data: UserSessionListItem[];
}

export function transformApiSession(
    apiSession: ApiSession,
    positionsData?: GetSessionResponse['data'],
    options?: { isShared?: boolean },
): GameSessionInternal {
    const startTime = new Date(apiSession.startedAt).getTime();
    const endTime = apiSession.completedAt ? new Date(apiSession.completedAt).getTime() : Date.now();

    const trail: SessionTrailPoint[] =
        positionsData?.positions.map(pos => ({
            x: parseFloat(pos.x),
            y: parseFloat(pos.y),
            z: parseFloat(pos.z),
            timestamp: parseFloat(pos.ts) * 1000,
        })) ?? [];

    const events = transformSessionEvents(positionsData?.events);

    return {
        uuid: apiSession.uuid,
        name: apiSession.name,
        titleId: apiSession.titleId,
        titleName: apiSession.titleName,
        startedAt: apiSession.startedAt,
        completedAt: apiSession.completedAt,
        status: apiSession.status,
        trail,
        events,
        duration: endTime - startTime,
        isShared: options?.isShared ?? false,
    };
}
